/**
 * Order activation + queue advancement. A unit has one active `intent` plus a
 * shift-built `queue` of follow-up orders. When the active order finishes
 * (movement arrival, node depletion, drop-off, build complete), `advanceOrder`
 * pulls the next queued order and `applyIntent` wires it up (repath + set the
 * gatherer/builder state the relevant system expects).
 */
import type { World } from "./world";
import type { MoveIntent } from "./components";
import type { Eid, Vec2 } from "./types";
import { findPath, approachPoint } from "./pathfinding";
import { domainOf, boardingShore, isNaval } from "./naval";

function repath(world: World, eid: number, tx: number, ty: number): void {
  const tr = world.transform.get(eid);
  const mv = world.move.get(eid);
  if (!tr || !mv) return;
  // ships route on water, land units on land — derived from the unit's class.
  // forPlayer lets the unit walk through its OWN gates (enemies are walled out).
  // bestEffort: a player MOVE to an unreachable spot still walks AS FAR as it can
  // (up to the rampart/obstacle) and slides, instead of freezing with no waypoint.
  mv.path = findPath(world, tr.pos.x, tr.pos.y, tx, ty, 6000, domainOf(world, eid), world.owner.get(eid)?.player, true, tr.radius) ?? [];
}

/**
 * Reference point used to interact with a building (deposit a load, hit the wall).
 * For a normal LAND building this is its centre; continuous pathfinding resolves
 * that blocked point to the nearest model-clear contact point. A building on WATER
 * (a dock) can't be stood on, so the unit works from the adjacent tile edge: a
 * LAND unit from the connected SHORE, a SHIP from the open water beside it.
 * Deterministic (dockShore/dockWater are). Null only if the building is gone.
 */
export function buildingStand(world: World, eid: Eid, building: Eid): Vec2 | null {
  const bTr = world.transform.get(building);
  if (!bTr) return null;
  const b = world.building.get(building);
  if (b && world.isWater(b.origin.x, b.origin.y)) {
    const adj = isNaval(world, eid)
      ? world.dockWater(b.origin.x, b.origin.y)   // ship: open water beside the pier
      : world.dockShore(b.origin.x, b.origin.y);  // land unit: the connected shore
    if (adj) {
      const ac = world.hexCenter(adj[0], adj[1]);
      // the outermost standable point of that tile, right against the pier — the
      // SAME spot a builder constructs the dock from, so deposit ↔ build agree.
      return approachPoint(ac.x, ac.y, bTr.pos.x, bTr.pos.y, world.size * 0.95);
    }
  }
  return { ...bTr.pos };
}

/**
 * Path to a BUILDING in one continuous route, stopping at the model-clear contact
 * point selected by pathfinding. A dock's stand point is already reachable shore
 * or open water. No synthetic centreward waypoint is appended: doing that after
 * pathfinding would drive the body back through the building texture.
 */
export function repathToDoor(world: World, eid: Eid, building: Eid): void {
  const tr = world.transform.get(eid);
  const mv = world.move.get(eid);
  const stand = buildingStand(world, eid, building);
  if (!tr || !mv || !stand) return;
  const path = findPath(world, tr.pos.x, tr.pos.y, stand.x, stand.y, 6000, domainOf(world, eid), world.owner.get(eid)?.player, false, tr.radius) ?? [];
  mv.path = path;
}

/** Make `intent` the unit's active order and configure systems to execute it. */
export function applyIntent(world: World, eid: Eid, intent: MoveIntent): void {
  const mv = world.move.get(eid);
  if (!mv) return;
  mv.intent = intent;
  mv.guard = undefined; // under orders → no defensive home post
  const g = world.gatherer.get(eid);
  const b = world.builder.get(eid);
  if (b && intent.kind !== "build") b.foundation = null;
  switch (intent.kind) {
    case "move":
    case "attackMove":
      if (g) g.node = null;
      repath(world, eid, intent.x, intent.y);
      break;
    case "attack": {
      if (g) g.node = null;
      const t = world.transform.get(intent.target);
      if (t) repath(world, eid, t.pos.x, t.pos.y);
      break;
    }
    case "gather": {
      const newRes = world.resource.get(intent.node)?.resource;
      if (g) {
        g.node = intent.node;
        // Carrying a DIFFERENT resource than the new target? Bank the partial
        // load FIRST (route to a drop-site), then harvest the new node — instead
        // of silently relabelling the old load as the new resource. The dropoff
        // handler resumes to g.node (this node) once the deposit completes.
        if (g.carrying > 1e-6 && g.resource && newRes && g.resource !== newRes) {
          g.state = "toDropoff";
          g.dropSite = null;
          break; // gathering system routes to the drop-site next tick
        }
        g.state = "toResource";
      }
      const n = world.transform.get(intent.node);
      if (n) repath(world, eid, n.pos.x, n.pos.y);
      break;
    }
    case "dropoff": {
      if (g) {
        g.dropSite = intent.dropSite;
        g.node = null;
        g.state = "toDropoff";
      }
      if (world.transform.has(intent.dropSite)) repathToDoor(world, eid, intent.dropSite);
      break;
    }
    case "embark": {
      if (g) g.node = null;
      const me = world.transform.get(eid);
      if (me) {
        if (world.building.has(intent.transport)) {
          // garrison a BUILDING (tower): walk right up to its MODEL, exactly like
          // attacking/repairing it — you garrison when your body touches the tower,
          // not from a tile away.
          repathToDoor(world, eid, intent.transport);
        } else {
          // a ship: walk to the shore on OUR OWN side beside it; transportSystem
          // boards once the hull is within reach across the waterline.
          const dest = boardingShore(world, intent.transport, me.pos.x, me.pos.y);
          if (dest) repath(world, eid, dest.x, dest.y);
        }
      }
      break;
    }
    case "unload": {
      // the transport sails toward the clicked LAND point; naval pathing snaps the
      // goal to the open water beside that shore. transportSystem disgorges on
      // arrival.
      repath(world, eid, intent.x, intent.y);
      break;
    }
    case "build": {
      if (g) g.node = null;
      if (b) b.foundation = intent.foundation;
      const f = world.transform.get(intent.foundation);
      const fb = world.building.get(intent.foundation);
      const tr = world.transform.get(eid);
      if (f && tr) {
        if (fb && world.isWater(fb.origin.x, fb.origin.y)) {
          // A dock sits on water — builders work from DRY LAND, they don't wade in.
          // Route to the shore hex the gangway attaches to (dockShore), then step to
          // that tile's OUTER EDGE facing the dock, so the worker stands right at the
          // water's edge against the dock tile (not in the middle of the shore tile).
          const shore = world.dockShore(fb.origin.x, fb.origin.y);
          if (shore) {
            const sc = world.hexCenter(shore[0], shore[1]);
            // 0.95·size from the dock centre toward the shore = just past the shared
            // edge onto the land side — the outermost standable point by the water.
            const edge = approachPoint(sc.x, sc.y, f.pos.x, f.pos.y, world.size * 0.95);
            const path = findPath(world, tr.pos.x, tr.pos.y, sc.x, sc.y, 6000, "land", world.owner.get(eid)?.player, false, tr.radius) ?? [];
            path.push(edge);
            mv.path = path;
          } else mv.path = [];
        } else repathToDoor(world, eid, intent.foundation);
      }
      break;
    }
  }
}

/** If the unit has queued orders, activate the next one. Returns true if it
 *  advanced; false (leaving `intent` untouched) when the queue is empty. */
export function advanceOrder(world: World, eid: Eid): boolean {
  const mv = world.move.get(eid);
  if (!mv || mv.queue.length === 0) return false;
  applyIntent(world, eid, mv.queue.shift()!);
  return true;
}

/** Apply an order now (replacing queue) or append it when shift-queued and the
 *  unit is already busy. Returns the resulting active intent kind. */
export function setOrder(world: World, eid: Eid, intent: MoveIntent, queued: boolean): void {
  const mv = world.move.get(eid);
  if (!mv) return;
  if (queued && (mv.intent || mv.queue.length)) {
    mv.queue.push(intent);
  } else {
    mv.queue = [];
    applyIntent(world, eid, intent);
  }
}
